import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { AlertTriangle, IndianRupee, ShieldCheck, UserPlus, Users } from "lucide-react";
import { api } from "../../api";
import { DESK } from "../../constants/testIds";
import { useDeskData } from "../../contexts/DeskDataContext";
import { formatCurrency, timeAwareGreeting, todayIso } from "../../lib/format";
import { fadeUp } from "../../lib/motion";
import { DrillDown } from "../../components/common/DrillDown";
import { StatCard } from "../../components/common/StatCard";
import { ErrorState } from "../../components/common/ErrorState";

const QUICK_ACTIONS = [
  { key: "checkin", label: "Check someone in", icon: ShieldCheck, path: "/desk/checkin" },
  { key: "new-member", label: "New member", icon: UserPlus, path: "/desk/members/new" },
  { key: "take-payment", label: "Take a payment", icon: IndianRupee, path: "/desk/money" },
];

// UX-FD-01 §1 Copilot Home. Two documented, deliberate substitutions (flagged per the prompt's
// own "surface the gap, don't fabricate" rule rather than reaching for a Manager-only endpoint
// Front Desk's role was never granted): "check-ins so far today" has no FD-permitted aggregate
// (the one that has it needs `pnl.view`, Manager-tier only) — shown as real-time occupancy
// ("Currently in branch") instead. "Revenue so far today" is computed client-side from
// `GET /ledger` (FD has `ledger.view`, auto branch-scoped by TenantGuard) rather than
// `/reports/cockpit-summary` (also `pnl.view`-gated). "Dues to collect" mirrors the EXACT
// precedent already established in OwnerCockpit.jsx (Promise.allSettled per-member balance
// fetch over a capped page) since no dues-ranking aggregate is FD-reachable either.
export default function DeskToday() {
  const { me, branchId } = useDeskData();
  const reducedMotion = useReducedMotion();
  const [showCollected, setShowCollected] = useState(false);

  const occupancyQ = useQuery({
    queryKey: ["desk-occupancy", branchId],
    queryFn: async () => (await api.get("/occupancy/live", { params: { branch_id: branchId } })).data,
    enabled: !!branchId,
  });

  const ledgerTodayQ = useQuery({
    queryKey: ["desk-ledger-today", branchId],
    queryFn: async () => (await api.get("/ledger", { params: { date_from: `${todayIso()}T00:00:00+00:00` } })).data.entries,
  });

  const atRiskQ = useQuery({
    queryKey: ["desk-at-risk"],
    queryFn: async () => (await api.get("/retention/at-risk")).data.members || [],
  });

  const trialsDueQ = useQuery({
    queryKey: ["desk-trials-due"],
    queryFn: async () => (await api.get("/leads", { params: { stage: "trial" } })).data.leads || [],
  });

  const membersQ = useQuery({
    queryKey: ["desk-members-for-dues"],
    queryFn: async () => (await api.get("/members", { params: { limit: 50 } })).data.members,
  });

  const duesQ = useQuery({
    queryKey: ["desk-dues", membersQ.data?.map((m) => m.id).join(",")],
    queryFn: async () => {
      const results = await Promise.allSettled(membersQ.data.map((m) => api.get(`/members/${m.id}/balance`)));
      return results
        .filter((r) => r.status === "fulfilled" && r.value.data.dues_balance > 0)
        .map((r) => r.value.data)
        .sort((a, b) => b.dues_balance - a.dues_balance)
        .slice(0, 5);
    },
    enabled: !!membersQ.data,
  });

  const revenueToday = useMemo(() => {
    if (!ledgerTodayQ.data) return null;
    return ledgerTodayQ.data
      .filter((e) => e.direction === "credit" && (e.type === "payment_cash" || e.type === "payment_digital"))
      .reduce((sum, e) => sum + e.amount, 0);
  }, [ledgerTodayQ.data]);

  const dueMemberName = (memberId) => membersQ.data?.find((m) => m.id === memberId)?.profile?.name || memberId;

  const collectedDetail = useMemo(() => {
    const rows = (ledgerTodayQ.data || [])
      .filter((e) => e.direction === "credit" && (e.type === "payment_cash" || e.type === "payment_digital"))
      .map((e) => ({
        at: e.at,
        member: (membersQ.data || []).find((m) => m.id === e.member_ref)?.profile?.name || e.member_ref || "-",
        type: e.type,
        amount: e.amount,
        membership_ref: e.membership_ref || "-",
        related_type: e.related_type || "-",
        related_ref: e.related_ref || "-",
        currency: e.currency,
        entry_id: e._id,
      }));
    return {
      metric: "collected-today",
      title: "Collected today",
      definition: "Cash and digital payments credited to the ledger since midnight today, at this branch.",
      total: rows.reduce((sum, r) => sum + r.amount, 0),
      total_format: "currency",
      total_label: "Total",
      row_count: rows.length,
      columns: [
        { key: "at", label: "Time", type: "datetime" },
        { key: "member", label: "Member", type: "text" },
        { key: "type", label: "Method", type: "badge" },
        { key: "amount", label: "Amount", type: "currency" },
        { key: "membership_ref", label: "Membership", type: "text" },
        { key: "related_type", label: "Source", type: "text" },
        { key: "related_ref", label: "Source ref", type: "text" },
        { key: "currency", label: "Currency", type: "text" },
        { key: "entry_id", label: "Ledger entry", type: "text" },
      ],
      rows,
    };
  }, [ledgerTodayQ.data, membersQ.data]);


  if (occupancyQ.isError) return <ErrorState error={occupancyQ.error} onRetry={() => occupancyQ.refetch()} title="Couldn't load Today" />;

  return (
    <div data-testid={DESK.todayPage} className="space-y-6">
      <motion.div {...fadeUp(reducedMotion)}>
        <h1 className="text-2xl font-bold" style={{ color: "#141416" }}>{timeAwareGreeting()}, {me?.name?.split(" ")[0]}</h1>
        <p className="text-sm mt-0.5" style={{ color: "#68686E" }}>Here's what needs you right now.</p>
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.03)} className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {QUICK_ACTIONS.map((a) => (
          <Link
            key={a.key}
            to={a.path}
            data-testid={DESK.quickActionCard(a.key)}
            className="flex items-center gap-3 rounded-[14px] p-5 font-semibold text-white transition-transform hover:scale-[1.02] active:scale-[0.99]"
            style={{ background: "#D6202B" }}
          >
            <a.icon size={22} /> {a.label}
          </Link>
        ))}
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.06)} className="grid grid-cols-2 gap-3">
        <StatCard testId={DESK.statCurrentlyIn} label="Currently in branch" value={occupancyQ.data?.count ?? 0} loading={occupancyQ.isLoading} index={0} />
        <StatCard testId={DESK.statCollectedToday} label="Collected today" value={revenueToday ?? 0} formatValue={formatCurrency} loading={ledgerTodayQ.isLoading} index={1} onClick={() => setShowCollected(true)} />
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Panel testId={DESK.atRiskList} icon={AlertTriangle} title="At-risk members" loading={atRiskQ.isLoading} empty="No at-risk members flagged.">
          {atRiskQ.data?.slice(0, 5).map((m) => (
            <Link key={m.member_ref} to={`/desk/members/${m.member_ref}`} className="block py-2 text-sm hover:opacity-70 transition-opacity" style={{ borderTop: "1px solid #EBEAE5" }}>
              <span className="font-medium" style={{ color: "#141416" }}>{m.member_name}</span>
              <span className="ml-2 text-xs" style={{ color: "#68686E" }}>{m.reason}</span>
            </Link>
          ))}
        </Panel>
        <Panel testId={DESK.duesList} icon={IndianRupee} title="Dues to collect" loading={duesQ.isLoading} empty="No outstanding dues right now.">
          {duesQ.data?.map((d) => (
            <Link key={d.member_id} to={`/desk/members/${d.member_id}`} className="flex items-center justify-between py-2 text-sm hover:opacity-70 transition-opacity" style={{ borderTop: "1px solid #EBEAE5" }}>
              <span className="font-medium" style={{ color: "#141416" }}>{dueMemberName(d.member_id)}</span>
              <span className="font-semibold" style={{ color: "#D6202B" }}>{formatCurrency(d.dues_balance)}</span>
            </Link>
          ))}
        </Panel>
        <Panel testId={DESK.trialsDueList} icon={Users} title="Trials to follow up" loading={trialsDueQ.isLoading} empty="No leads currently trialing.">
          {trialsDueQ.data?.slice(0, 5).map((l) => (
            <div key={l.id} className="py-2 text-sm" style={{ borderTop: "1px solid #EBEAE5" }}>
              <span className="font-medium" style={{ color: "#141416" }}>{l.contact?.name}</span>
              <span className="ml-2 text-xs" style={{ color: "#68686E" }}>{l.contact?.phone}</span>
            </div>
          ))}
        </Panel>
      </div>

      {showCollected && (
        <DrillDown localData={collectedDetail} onClose={() => setShowCollected(false)} />
      )}
    </div>
  );
}

function Panel({ testId, icon: Icon, title, loading, empty, children }) {
  const hasChildren = Array.isArray(children) ? children.some(Boolean) : !!children;
  return (
    <div data-testid={testId} className="rounded-[14px] p-4" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
      <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide mb-1" style={{ color: "#68686E" }}>
        <Icon size={13} /> {title}
      </p>
      {loading && <p className="text-sm py-2" style={{ color: "#68686E" }}>Loading…</p>}
      {!loading && !hasChildren && <p className="text-sm py-2" style={{ color: "#68686E" }}>{empty}</p>}
      {!loading && children}
    </div>
  );
}
